import React, { useState } from "react";
import { MoreVert as MoreVertIcon } from "@mui/icons-material";
import { Chip, IconButton, Menu, MenuItem, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import { useConfirm } from "material-ui-confirm";
import { useTranslation } from "react-i18next";

import { BackgroundConcentration } from "src/types";
import { isDisabled } from "src/utils/permission.utils";
import { StickyTable } from "./StickyTable";
import { useBCSetAsDefaultMutation, useBCDeleteMutation } from "src/hooks/background-concentrations";
import { IsVisible } from "./permissions";

interface Props {
  className?: string;
  data: BackgroundConcentration[];
}

export function BackgroundConcentrationTable(props: Props) {
  const { data } = props;
  const { t } = useTranslation();

  return render();

  function render() {
    return (
      <StickyTable className={props.className}>
        <TableHead>
          <TableRow>
            <TableCell>{t("label.name")}</TableCell>
            <TableCell>{t("label.type")}</TableCell>
            <TableCell>{t("label.linkedCases")}</TableCell>
            <TableCell>{t("label.status")}</TableCell>
            <TableCell>{t("label.created")}</TableCell>
            <TableCell></TableCell>
            <TableCell></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((item) => (
            <TableRow key={item.id}>
              <TableCell component="th" scope="row">
                {item.name}
              </TableCell>
              <TableCell>{t(`label.backgroundConcentrationType.${item.backgroundConcentrationType}`)}</TableCell>
              <TableCell>{item.linkedCases.length}</TableCell>
              <TableCell>{t(`label.importStatus.${item.importStatus}`)}</TableCell>
              <TableCell>{t("label.dateCreated", { date: item.dateCreated })}</TableCell>
              <TableCell align="right">
                {item.defaultValue && <Chip label={t("label.default")} color="primary" variant="outlined" />}
              </TableCell>
              <TableCell align="right">
                <RowActions data={item} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </StickyTable>
    );
  }
}

function RowActions(props: { data: BackgroundConcentration }) {
  const {
    data,
    data: { permissions },
  } = props;
  const { t } = useTranslation();
  const confirm = useConfirm();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  // TODO: refactor into hook
  const setDefaultMutation = useBCSetAsDefaultMutation(data.id);

  // TODO: refactor into hook
  const deleteMutation = useBCDeleteMutation(data.id);

  function handleClick(event: React.MouseEvent<HTMLButtonElement>) {
    setAnchorEl(event.currentTarget);
  }

  function handleClose() {
    setAnchorEl(null);
  }

  function handleSetAsDefault() {
    setDefaultMutation.mutate();
    handleClose();
  }

  function handleDelete() {
    confirm({
      confirmationButtonProps: { autoFocus: true },
      title: t("label.areYouSure"),
      description: t("confirm.deleteBackground", { name: data.name }),
    })
      .then(() => deleteMutation.mutate())
      .finally(() => handleClose());
  }

  return render();

  function render() {
    return (
      <IsVisible permission={[permissions.delete, permissions.setDefault, permissions.update, permissions.upload]}>
        <IconButton onClick={handleClick} size="large">
          <MoreVertIcon />
        </IconButton>
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
          <IsVisible permission={permissions.update}>
            <MenuItem
              disabled
              // TODO: enable next prop when edit func enters the game
              // disabled={isDisabled(permissions.update)}
            >
              {t("label.edit")}
            </MenuItem>
          </IsVisible>
          <IsVisible permission={permissions.delete}>
            <MenuItem onClick={handleDelete} disabled={isDisabled(permissions.delete)}>
              {t("label.delete")}
            </MenuItem>
          </IsVisible>
          <IsVisible permission={permissions.setDefault}>
            <MenuItem onClick={handleSetAsDefault} disabled={isDisabled(permissions.setDefault)}>
              {t("label.setAsDefault")}
            </MenuItem>
          </IsVisible>
          {data.downloadUrl && (
            <MenuItem component="a" href={data.downloadUrl}>
              {t("label.download")}
            </MenuItem>
          )}
        </Menu>
      </IsVisible>
    );
  }
}
